import { useEffect, useState } from 'react';
import type { Profile } from '@lr/shared';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

type Row = Profile;

export function AdminTeam() {
  const { profile: me } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    apiFetch<Row[]>('/api/admin/users')
      .then((d) => setRows(d.filter((u) => u.role !== 'user')))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function setRole(userId: string, role: 'user' | 'admin' | 'super_admin') {
    setUpdating(userId);
    setError(null);
    try {
      await apiFetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdating(null);
    }
  }

  const canManage = me?.role === 'super_admin';

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Team</h1>
          <div className="sub">People with access to this admin.</div>
        </div>
      </div>

      {!canManage && (
        <div className="a-alert a-alert-info">
          Only super admins can grant or revoke admin access.
        </div>
      )}
      {error && <div className="a-alert a-alert-error">{error}</div>}

      <div className="a-card">
        {loading ? (
          <div className="a-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="a-empty">
            <div className="h">Just you</div>
            <div className="p">Add admins by promoting an existing customer.</div>
          </div>
        ) : (
          <div className="a-table-wrap">
            <table className="a-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th style={{ width: 140 }}>Role</th>
                  <th style={{ width: 130 }}>Joined</th>
                  {canManage && <th style={{ width: 200 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                      </div>
                      <div className="id">{u.email}</div>
                    </td>
                    <td>
                      <span
                        className={`badge ${u.role === 'super_admin' ? 'badge-dark' : 'badge-info'}`}
                      >
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString('en-GB')}</td>
                    {canManage && (
                      <td>
                        {u.id === me?.id ? (
                          <span style={{ fontSize: 12, color: 'var(--a-muted)' }}>You</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              className="btn-ghost"
                              disabled={updating === u.id}
                              onClick={() => void setRole(u.id, 'user')}
                            >
                              Demote to user
                            </button>
                            {u.role === 'admin' && (
                              <button
                                className="btn-ghost"
                                disabled={updating === u.id}
                                onClick={() => void setRole(u.id, 'super_admin')}
                              >
                                Make super
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
